import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import type { DailyUptime, Incident, IncidentStyle } from "@shared/types";
import { cn, formatUptime } from "@/lib/utils";

const DOT_BY_STYLE: Record<IncidentStyle, string> = {
  info: "bg-status-maintenance",
  warning: "bg-status-pending",
  danger: "bg-status-down",
  success: "bg-status-up",
};

const POPUP_WIDTH = 264;

function barColor(day: DailyUptime): string {
  if (day.uptime == null) return "bg-muted";
  if (day.down === 0) return "bg-status-up";
  if (day.uptime >= 0.99) return "bg-status-pending";
  return "bg-status-down";
}

/** "2026-07-22" (UTC day from the API) -> "Wed, Jul 22, 2026" */
function dayLabel(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function statusLine(day: DailyUptime): string {
  if (day.uptime == null) return "No data for this day";
  const failed = day.down ? ` · ${day.down} failed check${day.down === 1 ? "" : "s"}` : "";
  return `${formatUptime(day.uptime)} uptime${failed}`;
}

interface HoverState {
  day: DailyUptime;
  /** viewport coords of the hovered bar's top-center */
  x: number;
  y: number;
}

function BarPopup({ hover, incidents }: { hover: HoverState; incidents: Incident[] }) {
  const margin = 8;
  const half = POPUP_WIDTH / 2;
  const left = Math.min(Math.max(hover.x, margin + half), window.innerWidth - margin - half);
  return createPortal(
    <div
      role="tooltip"
      className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-full rounded-lg border bg-popover p-3 text-xs text-popover-foreground shadow-md"
      style={{ left, top: hover.y - 8, width: POPUP_WIDTH }}
    >
      <p className="text-sm font-medium">{dayLabel(hover.day.day)}</p>
      <p className="mt-0.5 text-muted-foreground">{statusLine(hover.day)}</p>
      {incidents.length > 0 && (
        <ul className="mt-2 space-y-1.5 border-t pt-2">
          {incidents.map((incident) => (
            <li key={incident.id} className="flex items-start gap-2">
              <span className={cn("mt-1 size-2 shrink-0 rounded-full", DOT_BY_STYLE[incident.style])} />
              <span className="min-w-0 leading-4">{incident.title}</span>
            </li>
          ))}
        </ul>
      )}
    </div>,
    document.body,
  );
}

function Bars({
  days,
  incidentsByDay,
  className,
}: {
  days: DailyUptime[];
  incidentsByDay: Map<string, Incident[]>;
  className?: string;
}) {
  const [hover, setHover] = useState<HoverState | null>(null);

  // Single delegated handler + one popup — no per-bar tooltip instances.
  function handleOver(event: React.MouseEvent<HTMLDivElement>) {
    const target = event.target as HTMLElement;
    const index = target.dataset.idx;
    if (index == null) return;
    const day = days[Number(index)];
    if (!day) return;
    const rect = target.getBoundingClientRect();
    setHover((prev) => (prev?.day.day === day.day ? prev : { day, x: rect.left + rect.width / 2, y: rect.top }));
  }

  return (
    <div
      className={cn("h-7 gap-[3px]", className)}
      aria-label="Daily uptime history"
      onMouseOver={handleOver}
      onMouseLeave={() => setHover(null)}
    >
      {days.map((day, index) => (
        <span
          key={day.day}
          data-idx={index}
          aria-label={`${dayLabel(day.day)}: ${statusLine(day)}`}
          className={cn(
            "min-w-[3px] flex-1 rounded-[3px] transition-transform hover:scale-y-110",
            barColor(day),
          )}
        />
      ))}
      {hover && <BarPopup hover={hover} incidents={incidentsByDay.get(hover.day.day) ?? []} />}
    </div>
  );
}

/** DeepSeek-style segmented uptime pills: 90 days on desktop, 30 on mobile. */
export function DailyBars({ daily, incidents = [] }: { daily: DailyUptime[]; incidents?: Incident[] }) {
  // Bars use UTC day strings, so bucket incidents by their UTC calendar day.
  const incidentsByDay = useMemo(() => {
    const map = new Map<string, Incident[]>();
    for (const incident of incidents) {
      const key = new Date(incident.createdAt).toISOString().slice(0, 10);
      map.get(key)?.push(incident) ?? map.set(key, [incident]);
    }
    return map;
  }, [incidents]);

  return (
    <>
      <Bars days={daily} incidentsByDay={incidentsByDay} className="hidden sm:flex" />
      <Bars days={daily.slice(-30)} incidentsByDay={incidentsByDay} className="flex sm:hidden" />
    </>
  );
}
